import type { AdsAccountStatus, BindingMode, SiteStatus } from '@adsp/api-contract'
import type { LucideIcon } from 'lucide-react'
import {
  Bot,
  CheckCheck,
  CircleCheck,
  CircleSlash,
  Eye,
  FileClock,
  Inbox,
  KeyRound,
  Layers,
  Lightbulb,
  Loader2,
  PauseCircle,
  Radio,
  Unplug,
  XCircle,
} from 'lucide-react'

export type StatusTone =
  | 'neutral'
  | 'info'
  | 'processing'
  | 'ready'
  | 'pending'
  | 'success'
  | 'danger'
  | 'warning'

export interface StatusMeta {
  tone: StatusTone
  icon: LucideIcon
  labelKey: string
  pulse?: boolean
}

/**
 * Generic free-string status → tone. Fallback used for statuses the API types
 * as open strings; known values get a tone, everything else falls back to
 * `neutral` (and is still rendered verbatim by the caller).
 */
export function statusTone(status: string): StatusTone {
  switch (status) {
    case 'active':
    case 'ready':
      return 'success'
    case 'paused':
    case 'pending':
      return 'pending'
    case 'error':
    case 'failed':
      return 'danger'
    default:
      return 'neutral'
  }
}

/** Site lifecycle → tone/icon/label. Exhaustive over SiteStatus. */
export const SITE_STATUS_META: Record<SiteStatus, StatusMeta> = {
  active: { tone: 'success', icon: Radio, labelKey: 'status.site.active' },
  paused: { tone: 'neutral', icon: PauseCircle, labelKey: 'status.site.paused' },
}

/** Google Ads account connection → tone/icon/label. Exhaustive. */
export const ADS_ACCOUNT_STATUS_META: Record<AdsAccountStatus, StatusMeta> = {
  connected: { tone: 'ready', icon: CircleCheck, labelKey: 'status.adsAccount.connected' },
  auth_failed: { tone: 'warning', icon: KeyRound, labelKey: 'status.adsAccount.auth_failed' },
  disconnected: { tone: 'neutral', icon: Unplug, labelKey: 'status.adsAccount.disconnected' },
}

/**
 * Autopilot mode per binding — how much the pipeline may mutate without a
 * human: suggest (proposals only) < supervised < full_auto_prudent.
 */
export const BINDING_MODE_META: Record<BindingMode, StatusMeta> = {
  suggest: { tone: 'info', icon: Lightbulb, labelKey: 'status.mode.suggest' },
  supervised: { tone: 'warning', icon: Eye, labelKey: 'status.mode.supervised' },
  full_auto_prudent: { tone: 'ready', icon: Bot, labelKey: 'status.mode.full_auto_prudent' },
}

/**
 * Change-proposal lifecycle. `proposed` / `auto_eligible` await a decision;
 * `applying` is the CAS pipeline in flight (pulse). Unknown statuses (the API
 * may add more) fall back via proposalStatusMeta().
 */
export const PROPOSAL_STATUS_META: Record<string, StatusMeta> = {
  proposed: { tone: 'pending', icon: Inbox, labelKey: 'status.proposal.proposed' },
  auto_eligible: { tone: 'ready', icon: Bot, labelKey: 'status.proposal.auto_eligible' },
  approved: { tone: 'success', icon: CircleCheck, labelKey: 'status.proposal.approved' },
  applying: { tone: 'processing', icon: Loader2, labelKey: 'status.proposal.applying', pulse: true },
  applied: { tone: 'success', icon: CheckCheck, labelKey: 'status.proposal.applied' },
  failed: { tone: 'danger', icon: XCircle, labelKey: 'status.proposal.failed' },
  rejected: { tone: 'neutral', icon: CircleSlash, labelKey: 'status.proposal.rejected' },
  superseded: { tone: 'neutral', icon: Layers, labelKey: 'status.proposal.superseded' },
}

export function proposalStatusMeta(status: string): StatusMeta {
  return (
    PROPOSAL_STATUS_META[status] ?? {
      tone: 'neutral',
      icon: Inbox,
      labelKey: 'status.proposal.unknown',
    }
  )
}

/**
 * Knowledge-source ingest status (open string server-side). In-flight values
 * pulse; anything unknown renders verbatim in neutral.
 */
export function knowledgeStatusMeta(status: string): StatusMeta {
  switch (status) {
    case 'pending':
    case 'queued':
      return { tone: 'pending', icon: FileClock, labelKey: 'status.knowledge.pending' }
    case 'ingesting':
    case 'processing':
      return {
        tone: 'processing',
        icon: Loader2,
        labelKey: 'status.knowledge.ingesting',
        pulse: true,
      }
    case 'ready':
    case 'ingested':
    case 'active':
      return { tone: 'success', icon: CircleCheck, labelKey: 'status.knowledge.ready' }
    case 'error':
    case 'failed':
      return { tone: 'danger', icon: XCircle, labelKey: 'status.knowledge.failed' }
    default:
      return { tone: 'neutral', icon: FileClock, labelKey: 'status.knowledge.unknown' }
  }
}

/**
 * Audit grade → tone (the campaign weather). Maps on the FIRST letter so a
 * hypothetical "B+" still tones as B; unknown grades stay neutral.
 */
export function gradeTone(grade: string | null | undefined): StatusTone {
  switch (grade?.trim().charAt(0).toUpperCase()) {
    case 'A':
    case 'B':
      return 'success'
    case 'C':
    case 'D':
      return 'warning'
    case 'E':
    case 'F':
      return 'danger'
    default:
      return 'neutral'
  }
}

/** i18n label keys for the proposal kinds the UI knows; unknown kinds render verbatim. */
export const PROPOSAL_KIND_LABEL_KEYS: Record<string, string> = {
  add_negative_keyword: 'kind.add_negative_keyword',
  pause_keyword: 'kind.pause_keyword',
  pause_ad_group: 'kind.pause_ad_group',
  update_budget: 'kind.update_budget',
  update_final_url: 'kind.update_final_url',
  update_bidding_strategy: 'kind.update_bidding_strategy',
  add_sitelink: 'kind.add_sitelink',
  add_callout: 'kind.add_callout',
}

/** Prebuilt Tailwind utility strings per tone — components never hand-roll these. */
export const TONE_CLASSES: Record<
  StatusTone,
  { soft: string; solid: string; fg: string; dot: string }
> = {
  neutral: {
    soft: 'bg-tone-neutral-soft text-tone-neutral-fg',
    solid: 'bg-tone-neutral text-background',
    fg: 'text-tone-neutral-fg',
    dot: 'bg-tone-neutral',
  },
  info: {
    soft: 'bg-tone-info-soft text-tone-info-fg',
    solid: 'bg-tone-info text-background',
    fg: 'text-tone-info-fg',
    dot: 'bg-tone-info',
  },
  processing: {
    soft: 'bg-tone-processing-soft text-tone-processing-fg',
    solid: 'bg-tone-processing text-background',
    fg: 'text-tone-processing-fg',
    dot: 'bg-tone-processing',
  },
  ready: {
    soft: 'bg-tone-ready-soft text-tone-ready-fg',
    solid: 'bg-tone-ready text-background',
    fg: 'text-tone-ready-fg',
    dot: 'bg-tone-ready',
  },
  pending: {
    soft: 'bg-tone-pending-soft text-tone-pending-fg',
    solid: 'bg-tone-pending text-background',
    fg: 'text-tone-pending-fg',
    dot: 'bg-tone-pending',
  },
  success: {
    soft: 'bg-tone-success-soft text-tone-success-fg',
    solid: 'bg-tone-success text-background',
    fg: 'text-tone-success-fg',
    dot: 'bg-tone-success',
  },
  danger: {
    soft: 'bg-tone-danger-soft text-tone-danger-fg',
    solid: 'bg-tone-danger text-background',
    fg: 'text-tone-danger-fg',
    dot: 'bg-tone-danger',
  },
  warning: {
    soft: 'bg-tone-warning-soft text-tone-warning-fg',
    solid: 'bg-tone-warning text-background',
    fg: 'text-tone-warning-fg',
    dot: 'bg-tone-warning',
  },
}
